import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio/Radio';
import RadioGroup from '@material-ui/core/RadioGroup/RadioGroup';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import React, { ChangeEvent, PureComponent } from 'react';
import { injectIntl, FormattedMessage, InjectedIntlProps } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import pure from 'recompose/pure';
import { Action, Dispatch } from 'redux';
import messages from '../../../../../intl/messages';
import {
  AirFareOption,
  CarType,
  HotelRateOption,
  OptionTypes,
  RateTypes,
  SaviSelectIntlCarSegmentDetails,
  SaviSelectIntlHotelSegmentDetails,
  SegmentTypes,
} from '../../../../../models/itinerary';
import { AppState } from '../../../../../store/model';
import {
  isNew,
  isSaviSelectIntl,
  shouldShowPricing,
} from '../../../../../store/reducers/itinerary/selectors';
import { findSegment } from '../../../../../store/reducers/segment-details/selectors';
import { getRates } from '../../../../../store/reducers/segment-options/selectors';
import {
  getRateCurrencyCode,
  getRatePrice,
  getRateText,
  getSelectedRate,
} from '../../../../../utils/rates';
import { selectOptionFromRate } from '../../../../../store/reducers/selected-options/actions';
import { WithWidthProps } from '@material-ui/core/withWidth';
import { selectRate } from '../../../../../store/reducers/selected-rate/actions';
import {
  getCarDetailsForSaviSelectIntl,
  getRecommendCarWithinSegmentForSaviSelectIntl,
  getRecommendRateWithinSegmentForSaviSelectIntl,
  getSegmentRatesDetailsForSaviSelectIntl,
  getSelectedCarSegmentCombinationForSaviSelectIntl,
  getSelectedHotelSegmentCombinationForSaviSelectIntl,
  hasMultipleCarsWithinSegmentForSaviSelectIntl,
  hasMultipleRatesWithinSegmentForSaviSelectIntl,
} from '../../../../../store/reducers/itinerary/saviSelectIntlSelector';
import {
  setRecommendedCarItem,
  setRecommendedHotelItem,
} from '../../../../../store/reducers/itinerary/actions';

const styles = (theme: Theme) =>
  createStyles({
    icon: {
      color: theme.zeno.icons.dark.active,
    },
    formControl: {
      marginBottom: -1 * theme.spacing.unit,
    },
  });

interface OwnProps {
  type: SegmentTypes;
  optionId: string;
  segmentId: number;
  isMobile?: boolean;
}

interface StateProps {
  rates: RateTypes[];
  saviIntlRates?:
    | SaviSelectIntlHotelSegmentDetails[]
    | SaviSelectIntlCarSegmentDetails[];
  selectedRateId?: number;
  recommendedRateId?: number;
  showPrice: boolean;
  show: boolean;
  days?: number;
  nights?: number;
  isSaviSelectIntl: boolean;
}

interface DispatchProps {
  rateSelect(optionId: string, rateId: string, isRecommended: boolean): void;
  onRateSelectForSaviSelectIntl(rateId: number): void;
}

interface MergedProps extends OwnProps, StateProps, DispatchProps {
  onRateSelect(optionId: string, rateId: string): void;
}

interface RateDetailsProps
  extends MergedProps,
  WithStyles<typeof styles>,
  WithWidthProps,
  InjectedIntlProps { }

class RateDetailsBase extends PureComponent<RateDetailsProps> {
  public render() {
    const {
      rates,
      selectedRateId,
      saviIntlRates,
      recommendedRateId,
    } = this.props;
    return (
      <Grid container direction="column">
        <FormControl>
          <FormLabel>{this.renderSelectText()}</FormLabel>
          <RadioGroup
            value={(selectedRateId && selectedRateId.toString()) || ''}
            onChange={this.handleChange}
          >
            {!saviIntlRates
              ? rates.map(this.renderRadio)
              : this.renderSaviIntlRates(
                  saviIntlRates,
                  selectedRateId,
                  recommendedRateId,
                )}
          </RadioGroup>
        </FormControl>
      </Grid>
    );
  }

  private renderSelectText = () => {
    const { intl, type } = this.props;

    switch (type) {
      case 'Air':
        return intl.formatMessage(messages.flight.rate.select);
      case 'Hotel':
        return intl.formatMessage(messages.hotel.rate.select);
      case 'Car':
        return intl.formatMessage(messages.car.rate.select);
    }
  };

  private getMessage = (
    type: SegmentTypes,
  ): FormattedMessage.MessageDescriptor => {
    switch (type) {
      case 'Air':
        return messages.flight.rate.label;
      case 'Hotel':
        return messages.hotel.rate.label;
      case 'Car':
        return messages.car.rate.label;
    }
  };

  private getDaysOrNights = (type: SegmentTypes) => {
    const { days, nights } = this.props;
    switch (type) {
      case 'Air':
        return undefined;
      case 'Hotel':
        return nights;
      case 'Car':
        return days;
    }
  };

  private renderRadio = (rate: RateTypes) => {
    const {
      type,
      intl: { formatMessage, formatNumber },
      classes,
      showPrice,
      show,
    } = this.props;

    const { recommended } = rate;

    const label = formatMessage(this.getMessage(type), {
      optionText: getRateText(type, rate),
      recommended,
      price: formatNumber(
        getRatePrice(type, rate, this.getDaysOrNights(type))!,
        {
          style: 'currency',
          currency: getRateCurrencyCode(type, rate),
          currencyDisplay: 'narrowSymbol',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      ),
      showPrice,
    });

    const value = getRateId(type, rate);

    return (
      <FormControlLabel
        key={value}
        label={label}
        value={(value && value.toString()) || ''}
        className={classes.formControl}
        control={<Radio color="primary" />}
        disabled={!show}
      />
    );
  };

  private renderSaviIntlRates = (
    rates:
      | SaviSelectIntlHotelSegmentDetails[]
      | SaviSelectIntlCarSegmentDetails[],
    selectedRateId?: number,
    recommendedRateId?: number,
  ) => {
    const { type } = this.props;

    if (type === 'Hotel') {
      return (rates as SaviSelectIntlHotelSegmentDetails[]).map((rate) =>
        this.renderSaviIntlSelectRadio(rate, selectedRateId, recommendedRateId),
      );
    } else {
      return (rates as SaviSelectIntlCarSegmentDetails[]).map((rate) =>
        this.renderSaviIntlSelectRadio(rate, selectedRateId, recommendedRateId),
      );
    }
  };

  private renderSaviIntlSelectRadio = (
    rate: SaviSelectIntlHotelSegmentDetails | SaviSelectIntlCarSegmentDetails,
    selectedRateId?: number,
    recommendedRateId?: number,
  ) => {
    let value;
    let label;
    let recommended;
    const {
      type,
      intl: { formatMessage, formatNumber },
      classes,
      show,
      showPrice,
    } = this.props;

    const formatPrice = (totalPrice: number, currencyCode: string) => {
      return formatNumber(totalPrice, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    };

    if (type == 'Hotel') {
      const hotelRate = rate as SaviSelectIntlHotelSegmentDetails;
      label = formatMessage(this.getMessage('Hotel'), {
        optionText: hotelRate.roomDescription,
        recommended: recommendedRateId == hotelRate.roomId,
        price: formatPrice(
          hotelRate.fareInfo.totalCost,
          hotelRate.fareInfo.currencyCode,
        ),
        showPrice: showPrice,
      });
      value = hotelRate.roomId;
      recommended = selectedRateId == hotelRate.roomId;
    } else if (type == 'Car') {
      const carRate = rate as SaviSelectIntlCarSegmentDetails;
      label = formatMessage(this.getMessage('Car'), {
        optionText: `${carRate.carClass} ${carRate.carCategory} ${
          carRate.carModel.match(/[Aa]ny/) ? '' : carRate.carModel
        }`,
        recommended: recommendedRateId == carRate.carId,
        price: formatPrice(
          carRate.fareInfo.totalCost,
          carRate.fareInfo.currencyCode,
        ),
        showPrice: showPrice,
      });
      value = carRate.carId;
      recommended = selectedRateId == carRate.carId;
    }

    return (
      <FormControlLabel
        key={value}
        label={label}
        value={(value && value.toString()) || ''}
        className={classes.formControl}
        control={<Radio color="primary" checked={recommended} />}
        disabled={!show}
      />
    );
  };

  private handleChange = (_event: ChangeEvent<{}>, value: string) => {
    const {
      optionId,
      onRateSelect,
      onRateSelectForSaviSelectIntl,
    } = this.props;

    if (!isSaviSelectIntl) {
      onRateSelect(optionId, value);
    } else {
      onRateSelectForSaviSelectIntl(Number(value));
    }
  };
}

const getRateId = (type: string, rate: RateTypes): number => {
  switch (type) {
    case 'Air':
      return (rate as AirFareOption) && (rate as AirFareOption).fareId;
    case 'Hotel':
      return (rate as HotelRateOption) && (rate as HotelRateOption).rateId;
    case 'Car':
      return (rate as CarType) && (rate as CarType).carId;
  }
  return 0;
};

const mapStateToProps = (
  state: AppState,
  { segmentId, optionId, type, isMobile }: OwnProps,
): StateProps => {
  if (!isSaviSelectIntl(state)) {
    const { days, nights } = findSegment(segmentId)(state);
    const selectedRate = getSelectedRate(
      state,
      type,
      segmentId,
      optionId,
      isMobile || false,
    );
    return {
      rates: getRates(segmentId, optionId)(state) || [],
      selectedRateId: selectedRate && getRateId(type, selectedRate),
      showPrice: shouldShowPricing(state),
      show: isNew(state),
      days,
      nights,
      isSaviSelectIntl: isSaviSelectIntl(state),
    };
  } else {
    let selectedRateId = undefined;
    let recommendedRateId = undefined;
    let savintlRates = undefined;
    let show = true;
    if (type == 'Hotel') {
      selectedRateId = getSelectedHotelSegmentCombinationForSaviSelectIntl(
        segmentId,
      )(state)!.id;
      recommendedRateId = getRecommendRateWithinSegmentForSaviSelectIntl(
        segmentId,
      )(state)!.segments[0].id;
      savintlRates = getSegmentRatesDetailsForSaviSelectIntl(
        segmentId,
        Number(optionId),
      )(state);
      show = hasMultipleRatesWithinSegmentForSaviSelectIntl(
        segmentId,
        Number(optionId),
      )(state);
    } else if (type == 'Car') {
      selectedRateId = getSelectedCarSegmentCombinationForSaviSelectIntl(
        segmentId,
      )(state)!.landId;
      recommendedRateId = getRecommendCarWithinSegmentForSaviSelectIntl(
        segmentId,
      )(state)!.landId;
      savintlRates = getCarDetailsForSaviSelectIntl(
        segmentId,
        Number(optionId),
      )(state);
      show = hasMultipleCarsWithinSegmentForSaviSelectIntl(segmentId)(state);
    }

    if (state.itinerary.data.status === 'Accepted') {
      show = false;
    }

    return {
      rates: [],
      saviIntlRates: savintlRates,
      selectedRateId: selectedRateId,
      showPrice: shouldShowPricing(state),
      recommendedRateId: recommendedRateId,
      show: show,
      isSaviSelectIntl: isSaviSelectIntl(state),
    }
  }
};

const createOption = (
  type: SegmentTypes,
  optionId: string,
  rateId: string,
): OptionTypes => {
  switch (type) {
    case 'Air':
      return {
        fareId: Number(rateId),
        flightId: Number(optionId),
      };
    case 'Hotel':
      return {
        rateId: Number(rateId),
        hotelId: Number(optionId),
      };
    case 'Car':
      return {
        carId: Number(rateId),
        supplierCode: optionId,
      };
  }
};

const mapDispatchToProps = (
  dispatch: Dispatch<Action>,
  ownProps: OwnProps,
): DispatchProps => ({
  rateSelect: (optionId: string, rateId: string, isRecommended: boolean) => {
    if (ownProps.isMobile) {
      dispatch(selectRate(createOption(ownProps.type, optionId, rateId)!));
    } else {
      dispatch(
        selectOptionFromRate(
          ownProps.segmentId,
          createOption(ownProps.type, optionId, rateId),
          isRecommended,
        ),
      );
    }
  },
  onRateSelectForSaviSelectIntl: (rateId: number) => {
    if (ownProps.type == 'Hotel') {
      dispatch(
        setRecommendedHotelItem({
          id: rateId,
          landId: Number(ownProps.optionId),
          type0Id: ownProps.segmentId,
        }),
      );
    } else {
      dispatch(
        setRecommendedCarItem({
          id: -1,
          landId: Number(ownProps.optionId),
          type0Id: ownProps.segmentId,
        }),
      );
    }
  },
});

const mergeProps = (
  stateProps: StateProps,
  dispatchProps: DispatchProps,
  ownProps: OwnProps,
): MergedProps => {
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onRateSelect: (optionId: string, rateId: string) => {
      const rate = stateProps.rates.find(
        (r) => (r as AirFareOption).fareId === Number(rateId),
      );
      return dispatchProps.rateSelect(
        optionId,
        rateId,
        (rate && rate.recommended) || false,
      );
    },
  };
};

const enhance = compose<RateDetailsProps, OwnProps>(
  pure,
  injectIntl,
  connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps,
  ),
  withStyles(styles),
);

export const RateDetails = enhance(RateDetailsBase);

RateDetails.displayName = 'RateDetails';
